/**
 * Sale/Purchase API client — CONSUME the frozen backend endpoints.
 *
 * Every response body is validated at the ADAPTER layer here: numeric
 * money fields are coerced to bigint, discriminators are stamped, and
 * V1/V2 responses are shaped into the discriminated union defined in
 * `sale-types.ts`.
 *
 * NEVER duplicates SEP pricing. NEVER invents fields the wire didn't
 * send. NEVER treats an absent `spatial_pricing` key as `null`.
 */
import { API_BASE, ApiError } from "./api";
import { toBigInt } from "./money";
import type {
  ErrorEnvelope,
  Generation,
  PurchasesPage,
  SaleConfig,
  SaleConfigV1,
  SaleConfigV2,
  SaleQuote,
  SaleQuoteV1,
  SaleQuoteV2,
  SaleStatus,
  SaleStatusV1,
  SaleStatusV2,
} from "./sale-types";
import type { GridSelection } from "./grid-selection";

async function apiGet<T>(path: string): Promise<T> {
  const url = `${API_BASE}${path}`;
  const resp = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!resp.ok) {
    const b = (body as Partial<ErrorEnvelope>) || {};
    throw new ApiError(
      resp.status,
      b.code || "UNKNOWN",
      b.message || resp.statusText,
      b.details,
    );
  }
  return body as T;
}

// ---------------------------------------------------------------------
// Adapters — coerce wire → typed
// ---------------------------------------------------------------------
function readGeneration(raw: unknown): Generation | null {
  if (raw && typeof raw === "object" && "generation" in raw) {
    const g = (raw as { generation?: unknown }).generation;
    if (g === "v1" || g === "v2") return g;
  }
  return null;
}

function adaptSaleConfig(raw: unknown): SaleConfig {
  const gen = readGeneration(raw);
  const r = raw as Record<string, unknown>;
  if (gen === "v2") {
    const v2: SaleConfigV2 = {
      generation: "v2",
      configured: Boolean(r.configured),
      state: (r.state as SaleConfigV2["state"]) ?? "not_configured",
      chain_id: Number(r.chain_id),
      sale_contract_address: String(r.sale_contract_address),
      payment_token: String(r.payment_token),
      payment_token_decimals: Number(r.payment_token_decimals),
      parcel_contract: String(r.parcel_contract),
      deploy_block_number: Number(r.deploy_block_number),
      config_as_of_block: Number(r.config_as_of_block),
      base_primary_price_microusdc: toBigInt(r.base_primary_price_microusdc),
      multiplier_scale: toBigInt(r.multiplier_scale),
      zone_multiplier_standard: toBigInt(r.zone_multiplier_standard),
      zone_multiplier_premium: toBigInt(r.zone_multiplier_premium),
      zone_multiplier_vip: toBigInt(r.zone_multiplier_vip),
      capacity_standard: toBigInt(r.capacity_standard),
      capacity_premium: toBigInt(r.capacity_premium),
      capacity_vip_sellable: toBigInt(r.capacity_vip_sellable),
      capacity_grid_zero_locked: toBigInt(r.capacity_grid_zero_locked),
      capacity_reserve_locked: toBigInt(r.capacity_reserve_locked),
      max_parcel_standard: toBigInt(r.max_parcel_standard),
      max_parcel_premium: toBigInt(r.max_parcel_premium),
      max_parcel_vip: toBigInt(r.max_parcel_vip),
      grid_width: Number(r.grid_width),
      grid_height: Number(r.grid_height),
      max_parcel_width: Number(r.max_parcel_width),
      max_parcel_height: Number(r.max_parcel_height),
      max_parcel_area: Number(r.max_parcel_area),
    };
    return v2;
  }
  // V1 (or legacy config not yet promoted): pass through, no fake SEP.
  const v1: SaleConfigV1 = {
    generation: "v1",
    configured: Boolean(r.configured),
    state: (r.state as SaleConfigV1["state"]) ?? "not_configured",
    error_reason: (r.error_reason as string | null) ?? null,
    chain_id: r.chain_id == null ? null : Number(r.chain_id),
    sale_contract_address:
      r.sale_contract_address == null ? null : String(r.sale_contract_address),
    payment_token: r.payment_token == null ? null : String(r.payment_token),
    payment_token_decimals:
      r.payment_token_decimals == null ? null : Number(r.payment_token_decimals),
    parcel_contract:
      r.parcel_contract == null ? null : String(r.parcel_contract),
    deploy_block_number:
      r.deploy_block_number == null ? null : Number(r.deploy_block_number),
  };
  return v1;
}

function adaptSaleStatus(raw: unknown): SaleStatus {
  const gen = readGeneration(raw);
  const r = raw as Record<string, unknown>;
  if (gen === "v2") {
    const v2: SaleStatusV2 = {
      generation: "v2",
      state: r.state as SaleStatusV2["state"],
      data_status: (r.data_status as SaleStatusV2["data_status"]) ?? null,
      indexed_through_block:
        r.indexed_through_block == null
          ? null
          : Number(r.indexed_through_block),
      arithmetic_mismatch_count: Number(r.arithmetic_mismatch_count ?? 0),
      reconciliation_violation_count: Number(
        r.reconciliation_violation_count ?? 0,
      ),
      reconciliation_mismatch_count: Number(
        r.reconciliation_mismatch_count ?? 0,
      ),
      last_batch_error_reason:
        (r.last_batch_error_reason as string | null) ?? null,
      paused: Boolean(r.paused),
      sold_standard: toBigInt(r.sold_standard),
      sold_premium: toBigInt(r.sold_premium),
      sold_vip: toBigInt(r.sold_vip),
      status_as_of_block: Number(r.status_as_of_block),
    };
    return v2;
  }
  const v1: SaleStatusV1 = {
    generation: "v1",
    state: r.state as SaleStatusV1["state"],
    data_status: (r.data_status as SaleStatusV1["data_status"]) ?? null,
    indexed_through_block:
      r.indexed_through_block == null ? null : Number(r.indexed_through_block),
    arithmetic_mismatch_count: Number(r.arithmetic_mismatch_count ?? 0),
    reconciliation_violation_count: Number(
      r.reconciliation_violation_count ?? 0,
    ),
    reconciliation_mismatch_count: Number(
      r.reconciliation_mismatch_count ?? 0,
    ),
    last_batch_error_reason:
      (r.last_batch_error_reason as string | null) ?? null,
  };
  return v1;
}

function maybeBigInt(v: unknown): bigint | null {
  return v == null ? null : toBigInt(v);
}

function maybeNumber(v: unknown): number | null {
  return v == null ? null : Number(v);
}

function adaptSaleQuote(raw: unknown): SaleQuote {
  const r = raw as Record<string, unknown>;
  const isV2 = typeof r.quote_result !== "undefined";
  if (isV2) {
    const v2: SaleQuoteV2 = {
      generation: "v2",
      advisory: Boolean(r.advisory ?? true),
      x: Number(r.x),
      y: Number(r.y),
      w: Number(r.w),
      h: Number(r.h),
      slots: Number(r.slots),
      quote_result: r.quote_result as SaleQuoteV2["quote_result"],
      zone: String(r.zone),
      sellable: Boolean(r.sellable),
      unit_price_microusdc: maybeBigInt(r.unit_price_microusdc),
      total_price_microusdc: maybeBigInt(r.total_price_microusdc),
      zone_multiplier: maybeBigInt(r.zone_multiplier),
      scarcity_tier: maybeNumber(r.scarcity_tier),
      scarcity_multiplier: maybeBigInt(r.scarcity_multiplier),
      effective_multiplier: maybeBigInt(r.effective_multiplier),
      sold_before: maybeBigInt(r.sold_before),
      sellable_capacity: maybeBigInt(r.sellable_capacity),
      next_threshold_slots_left: maybeNumber(r.next_threshold_slots_left),
      next_tier_unit_price_microusdc: maybeBigInt(
        r.next_tier_unit_price_microusdc,
      ),
      zone_breakdown: (r.zone_breakdown as SaleQuoteV2["zone_breakdown"]) ?? {
        standard: 0,
        premium: 0,
        vip: 0,
        grid_zero: 0,
        reserve: 0,
      },
      payment_token: String(r.payment_token),
      quote_as_of_block: Number(r.quote_as_of_block),
      sale_authority:
        r.sale_authority && typeof r.sale_authority === "object"
          ? {
              chain_id: Number((r.sale_authority as Record<string, unknown>).chain_id),
              sale_contract_address: String(
                (r.sale_authority as Record<string, unknown>).sale_contract_address,
              ),
            }
          : null,
    };
    return v2;
  }
  const v1: SaleQuoteV1 = {
    generation: "v1",
    advisory: Boolean(r.advisory ?? true),
    x: Number(r.x),
    y: Number(r.y),
    w: Number(r.w),
    h: Number(r.h),
    slots: Number(r.slots),
    unit_price_microusdc: toBigInt(r.unit_price_microusdc),
    total_price_microusdc: toBigInt(r.total_price_microusdc),
    payment_token: String(r.payment_token),
    quote_as_of_block: Number(r.quote_as_of_block),
  };
  return v1;
}

function adaptPurchasesPage(raw: unknown): PurchasesPage {
  const r = raw as Record<string, unknown>;
  const rows = (r.purchases as Record<string, unknown>[]) ?? [];
  return {
    purchases: rows.map((p) => {
      const base = {
        id: String(p.id),
        chain_id: Number(p.chain_id),
        sale_contract_address: String(p.sale_contract_address),
        block_number: Number(p.block_number),
        block_hash: String(p.block_hash),
        tx_hash: String(p.tx_hash),
        log_index: Number(p.log_index),
        buyer: String(p.buyer),
        parcel_id: Number(p.parcel_id),
        x: Number(p.x),
        y: Number(p.y),
        w: Number(p.w),
        h: Number(p.h),
        slots: Number(p.slots),
        unit_price_microusdc: toBigInt(p.unit_price_microusdc),
        total_paid: toBigInt(p.total_paid),
        payment_token: String(p.payment_token),
        canonical: Boolean(p.canonical),
        generation: (p.generation as Generation) ?? "v1",
      };
      // spatial_pricing is OMITTED on the wire when absent — do NOT
      // synthesize null. Only attach when the key really exists.
      if ("spatial_pricing" in p && p.spatial_pricing) {
        const sp = p.spatial_pricing as Record<string, unknown>;
        return {
          ...base,
          spatial_pricing: {
            zone: sp.zone as "STANDARD" | "PREMIUM" | "VIP_CORE",
            scarcity_tier: Number(sp.scarcity_tier),
            sold_before: Number(sp.sold_before),
            sold_after: Number(sp.sold_after),
            unit_price_microusdc: toBigInt(sp.unit_price_microusdc),
          },
        };
      }
      return base;
    }),
    next_cursor: (r.next_cursor as string | null) ?? null,
    page_size: Number(r.page_size),
  };
}

// ---------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------
export const saleApi = {
  config: async (): Promise<SaleConfig> => adaptSaleConfig(
    await apiGet<unknown>("/api/v1/sale/config"),
  ),
  status: async (): Promise<SaleStatus> => adaptSaleStatus(
    await apiGet<unknown>("/api/v1/sale/status"),
  ),
  quote: async (sel: GridSelection): Promise<SaleQuote> => {
    const qs = new URLSearchParams({
      x: String(sel.x),
      y: String(sel.y),
      w: String(sel.w),
      h: String(sel.h),
    });
    const q = adaptSaleQuote(
      await apiGet<unknown>(`/api/v1/sale/quote?${qs.toString()}`),
    );
    // WO#4a §10 — propagate the sale authority observation to the
    // frontend `SaleAuthorityProvider` via a decoupled CustomEvent.
    // The provider listens on `window` and updates atomically; this
    // avoids threading a callback through every quote-issuing hook.
    if (typeof window !== "undefined" && q.generation === "v2" && q.sale_authority) {
      try {
        window.dispatchEvent(
          new CustomEvent("thegrid:sale-authority-observed", {
            detail: {
              chain_id: q.sale_authority.chain_id,
              sale_contract_address: q.sale_authority.sale_contract_address,
            },
          }),
        );
      } catch {
        /* ignore CustomEvent unsupported */
      }
    }
    return q;
  },
  purchases: async (params: {
    buyer?: string;
    parcel_id?: number;
    canonical?: boolean;
    generation?: "all" | "v1" | "v2" | "active";
    page_size?: number;
    cursor?: string;
  } = {}): Promise<PurchasesPage> => {
    const qs = new URLSearchParams();
    if (params.buyer) qs.set("buyer", params.buyer);
    if (params.parcel_id != null) qs.set("parcel_id", String(params.parcel_id));
    if (params.canonical != null)
      qs.set("canonical", params.canonical ? "true" : "false");
    if (params.generation) qs.set("generation", params.generation);
    if (params.page_size != null) qs.set("page_size", String(params.page_size));
    if (params.cursor) qs.set("cursor", params.cursor);
    const suffix = qs.toString();
    return adaptPurchasesPage(
      await apiGet<unknown>(
        `/api/v1/purchases${suffix ? `?${suffix}` : ""}`,
      ),
    );
  },
};

export { adaptSaleConfig, adaptSaleStatus, adaptSaleQuote, adaptPurchasesPage };
